import { ActionFunctionArgs, LoaderFunctionArgs, redirect } from "react-router-dom";
import { myAxios } from "src/components/common/functions";
import { fields } from "./LeadFilterForm";
import { appendField } from "../common/FilterFrame/FilterField";
import { saveAs } from "file-saver";
import { buildDefaultState } from "../common/FilterFrame/FilterModal";
import { extraFields } from "./LeadList";

export async function leadsLoader({ request }: LoaderFunctionArgs) {
    const url = new URL(request.url);
    const pageParam  = url.searchParams.get("page");
    const limitParam = url.searchParams.get("limit");
    const noOrganizationAssociatedParam = url.searchParams.get("noOrganizationAssociated");
    let shouldRedirect = false;
    for (const [key] of url.searchParams) {
        if(![...Object.keys(buildDefaultState(fields)), ...extraFields].includes(key)){
            url.searchParams.delete(key);
            shouldRedirect = true;
        }
    }
    if (!pageParam) {
        url.searchParams.set("page", "1");
        shouldRedirect = true;
    }
    if (!limitParam) {
        url.searchParams.set("limit", "10");
        shouldRedirect = true;
    }
    if (noOrganizationAssociatedParam && noOrganizationAssociatedParam !== "1") {
        url.searchParams.delete("noOrganizationAssociated");
        shouldRedirect = true;
    }
    if (shouldRedirect) throw redirect(url.pathname + "?" + url.searchParams.toString());
    const page  = Number(url.searchParams.get("page"));
    const limit = Number(url.searchParams.get("limit"));
    const formData = new FormData();
    formData.append("page", String(page));
    formData.append("limit", String(limit));
    if(noOrganizationAssociatedParam) formData.append("noOrganizationAssociated", noOrganizationAssociatedParam);
    for (const field of fields) {
        appendField(formData, url.searchParams, field);
    }
    const res = await myAxios({ fileName: "leads/readLeads.php", formData });
    if(!res) throw redirect("..");
    return res.data;
}

export async function leadLoader({ params }: LoaderFunctionArgs) {
    const { id } = params;
    if (!id) {
    throw redirect(`/Leads`);
    }
    const formData = new FormData();
    formData.append("id", id);
    const res = await myAxios({
        fileName: "leads/readLead.php",
        formData
    });
    if (!res || !res.data) throw redirect("..");
    return res.data;
}
const urlMap = { "Add": "leads/createLead.php", "Edit": "leads/updateLead.php" };

export async function leadAction({
    request
}: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const formData = await request.formData();
    const action = formData.get("action") as keyof typeof urlMap;
    const url = urlMap[action];
    if(!url){
        window.alert("Invalid Keyword");
        return;
    }
    const res = await myAxios({ fileName: url, formData, signal: controller.signal });
    if(!res) return;
    if(action === "Add"){
        return redirect(`/Leads/${res.data.id}`);
    }
    return redirect("..");
}

export async function deleteLeadAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const formData = await request.formData();
    if(!formData.get("id")){
        throw new Response("Missing id", { status: 400 });
    }
    const res = await myAxios({
        fileName: "leads/deleteLead.php",
        formData: formData, 
        signal: controller.signal
    });
    if(!res) throw redirect("..");
    throw redirect("../..");
}

export async function voidLeadAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const formData = await request.formData();
    if(!formData.get("id")){
        throw new Response("Missing id", { status: 400 });
    }
    await myAxios({
        fileName: "leads/voidLead.php",
        formData: formData, 
        signal: controller.signal
    });
    throw redirect("..");
}

export async function validateLeadAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const formData = await request.formData();
    if(!formData.get("id")){
        throw new Response("Missing id", { status: 400 });
    }
    await myAxios({
        fileName: "leads/validateLead.php",
        formData: formData, 
        signal: controller.signal
    });
    throw redirect("..");
}

export async function sendLeadEmailAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const url = new URL(request.url);
    const formData = new FormData();
    formData.append("limit", "75");
    formData.append("sleepSeconds", "2");
    const noOrganizationAssociatedParam = url.searchParams.get("noOrganizationAssociated");
    if(noOrganizationAssociatedParam === "1"){
        formData.append("noOrganizationAssociated", noOrganizationAssociatedParam);
    }
    for(const field of fields){
        appendField(formData, url.searchParams, field);
    }
    await myAxios({
        fileName: "leads/sendLeadEmail.php",
        formData,
        signal: controller.signal
    });
}

export async function sentEmailsAction({ request }: ActionFunctionArgs) {
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const formData = await request.formData();
    if(!formData.get("limit")){
        formData.append("limit", "200");
    }
    const res = await myAxios({
        fileName: "leads/readSentEmails.php",
        formData,
        signal: controller.signal
    });
    if(!res) return null;
    return res.data;
}

export async function leadExportAction({ request }: ActionFunctionArgs){
    const controller = new AbortController();
    request.signal.addEventListener("abort", () => {
        controller.abort();
    });
    const url = new URL(request.url);
    const formData = await request.formData();
    for(const field of fields){
        appendField(formData, url.searchParams, field);
    }
    const res = await myAxios({
        fileName: `/leads/exportLeads.php`,
        formData, 
        responseType: "blob", 
        signal: controller.signal
    });
    if(!res) throw redirect(`..?${url.searchParams.toString()}`);
    const disposition = res.headers["content-disposition"];
    let filename = "leads.xlsx";
    if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match) filename = decodeURIComponent(match[1]);
    }
    saveAs(
        new Blob([res.data], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        filename
    );
    throw redirect(`..?${url.searchParams.toString()}`);
}
